/**
 * NotificationEngine — unified notification system with alert rules,
 * deadline scanning, persistence, and dispatch to email/SMS/in-app channels.
 *
 * Consumes workflow tasks + resident data to generate actionable alerts.
 */

import LocalStorageService from '../storage/LocalStorageService';
import EmailService from '../integrations/EmailService';
import SMSService from '../integrations/SMSService';
import { residents as mockResidents } from '../../data/mockData';
import { listTasks } from '../workflow/TaskService';
import { acerSlaFromAdmission } from '../workflow/SLAEngine';
import { TASK_STATUS } from '../../workflow/taskModel';

const STORAGE_KEY = 'mf_notifications_v2';
const RULES_KEY = 'mf_notification_rules';

export const NOTIFICATION_TYPE = {
  ACER_DEADLINE: 'acer_deadline',
  ASSESSMENT_OVERDUE: 'assessment_overdue',
  CARE_PLAN_REVIEW: 'care_plan_review',
  CREDENTIAL_EXPIRY: 'credential_expiry',
  TASK_OVERDUE: 'task_overdue',
  TASK_ESCALATED: 'task_escalated',
  DOCUMENT_PENDING: 'document_pending',
  SETTLING_TRANSITION: 'settling_transition',
  WORKFLOW_COMPLETE: 'workflow_complete',
  SYSTEM: 'system',
};

export const NOTIFICATION_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  CRITICAL: 'critical',
  SUCCESS: 'success',
};

export const NOTIFICATION_CHANNEL = {
  IN_APP: 'in_app',
  EMAIL: 'email',
  SMS: 'sms',
};

function generateId() {
  return `notif_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function todayIso() {
  return new Date().toISOString().split('T')[0];
}

class NotificationEngine {
  constructor() {
    this._listeners = [];
  }

  /** Subscribe to new notifications (for React in-app bell). */
  subscribe(fn) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter((l) => l !== fn);
    };
  }

  _emit(notification) {
    this._listeners.forEach((fn) => {
      try { fn(notification); } catch { /* swallow */ }
    });
  }

  // ── CRUD ──────────────────────────────────────────────────────────────

  getAll(filters = {}) {
    let items = LocalStorageService.get(STORAGE_KEY, []);
    if (filters.userId) items = items.filter((n) => n.userId === filters.userId || n.userId === '*');
    if (filters.type) items = items.filter((n) => n.type === filters.type);
    if (filters.read !== undefined) items = items.filter((n) => n.read === filters.read);
    if (filters.facilityId) items = items.filter((n) => n.facilityId === filters.facilityId);
    items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return items;
  }

  getUnreadCount(userId) {
    return this.getAll({ userId, read: false }).length;
  }

  markRead(notificationId) {
    const items = LocalStorageService.get(STORAGE_KEY, []);
    const idx = items.findIndex((n) => n.id === notificationId);
    if (idx === -1) return false;
    items[idx].read = true;
    items[idx].readAt = new Date().toISOString();
    LocalStorageService.set(STORAGE_KEY, items);
    return true;
  }

  markAllRead(userId) {
    const items = LocalStorageService.get(STORAGE_KEY, []);
    let changed = 0;
    const now = new Date().toISOString();
    items.forEach((n) => {
      if ((n.userId === userId || n.userId === '*') && !n.read) {
        n.read = true;
        n.readAt = now;
        changed++;
      }
    });
    if (changed > 0) LocalStorageService.set(STORAGE_KEY, items);
    return changed;
  }

  dismiss(notificationId) {
    const items = LocalStorageService.get(STORAGE_KEY, []);
    const filtered = items.filter((n) => n.id !== notificationId);
    LocalStorageService.set(STORAGE_KEY, filtered);
    return filtered.length < items.length;
  }

  /**
   * Create and persist a notification, emit to listeners.
   * @returns {Object} notification
   */
  create({
    userId = '*',
    facilityId = null,
    residentId = null,
    type = NOTIFICATION_TYPE.SYSTEM,
    severity = NOTIFICATION_SEVERITY.INFO,
    title,
    message,
    actionUrl = null,
    actionLabel = null,
    channels = [NOTIFICATION_CHANNEL.IN_APP],
    metadata = {},
  }) {
    const notification = {
      id: generateId(),
      userId,
      facilityId,
      residentId,
      type,
      severity,
      title,
      message,
      actionUrl,
      actionLabel,
      channels,
      metadata,
      read: false,
      readAt: null,
      createdAt: new Date().toISOString(),
    };

    const items = LocalStorageService.get(STORAGE_KEY, []);
    items.unshift(notification);

    // Cap at 500 notifications to prevent storage bloat
    if (items.length > 500) items.length = 500;
    LocalStorageService.set(STORAGE_KEY, items);

    this._emit(notification);
    this._dispatch(notification);

    if (process.env.NODE_ENV === 'development') {
      console.debug('[NotificationEngine] created', { type, severity, title });
    }
    return notification;
  }

  // ── ALERT RULES ───────────────────────────────────────────────────────

  /**
   * Get default alert rule definitions.
   * Each rule defines: type, scan logic, severity thresholds.
   */
  static get DEFAULT_RULES() {
    return [
      {
        id: 'acer_14d',
        type: NOTIFICATION_TYPE.ACER_DEADLINE,
        label: 'ACER deadline — 14 days',
        daysThreshold: 14,
        severity: NOTIFICATION_SEVERITY.INFO,
        enabled: true,
      },
      {
        id: 'acer_7d',
        type: NOTIFICATION_TYPE.ACER_DEADLINE,
        label: 'ACER deadline — 7 days',
        daysThreshold: 7,
        severity: NOTIFICATION_SEVERITY.WARNING,
        enabled: true,
      },
      {
        id: 'acer_3d',
        type: NOTIFICATION_TYPE.ACER_DEADLINE,
        label: 'ACER deadline — 3 days',
        daysThreshold: 3,
        severity: NOTIFICATION_SEVERITY.CRITICAL,
        enabled: true,
      },
      {
        id: 'assessment_3d',
        type: NOTIFICATION_TYPE.ASSESSMENT_OVERDUE,
        label: 'Assessment overdue — 3 days',
        daysThreshold: 3,
        severity: NOTIFICATION_SEVERITY.WARNING,
        enabled: true,
      },
      {
        id: 'assessment_7d',
        type: NOTIFICATION_TYPE.ASSESSMENT_OVERDUE,
        label: 'Assessment overdue — 7 days',
        daysThreshold: 7,
        severity: NOTIFICATION_SEVERITY.WARNING,
        enabled: true,
      },
      {
        id: 'assessment_14d',
        type: NOTIFICATION_TYPE.ASSESSMENT_OVERDUE,
        label: 'Assessment overdue — 14 days',
        daysThreshold: 14,
        severity: NOTIFICATION_SEVERITY.CRITICAL,
        enabled: true,
      },
      {
        id: 'careplan_7d',
        type: NOTIFICATION_TYPE.CARE_PLAN_REVIEW,
        label: 'Care plan review due — 7 days',
        daysThreshold: 7,
        severity: NOTIFICATION_SEVERITY.WARNING,
        enabled: true,
      },
      {
        id: 'credential_30d',
        type: NOTIFICATION_TYPE.CREDENTIAL_EXPIRY,
        label: 'Credential expiry — 30 days',
        daysThreshold: 30,
        severity: NOTIFICATION_SEVERITY.INFO,
        enabled: true,
      },
      {
        id: 'credential_14d',
        type: NOTIFICATION_TYPE.CREDENTIAL_EXPIRY,
        label: 'Credential expiry — 14 days',
        daysThreshold: 14,
        severity: NOTIFICATION_SEVERITY.WARNING,
        enabled: true,
      },
      {
        id: 'credential_7d',
        type: NOTIFICATION_TYPE.CREDENTIAL_EXPIRY,
        label: 'Credential expiry — 7 days',
        daysThreshold: 7,
        severity: NOTIFICATION_SEVERITY.CRITICAL,
        enabled: true,
      },
    ];
  }

  static NOTIFICATION_EMAIL_TEMPLATES = {
    [NOTIFICATION_TYPE.ACER_DEADLINE]: 'tpl_care_statement',
    [NOTIFICATION_TYPE.ASSESSMENT_OVERDUE]: 'tpl_care_statement',
    [NOTIFICATION_TYPE.CARE_PLAN_REVIEW]: 'tpl_care_statement',
    [NOTIFICATION_TYPE.CREDENTIAL_EXPIRY]: 'tpl_credential_expiry',
    [NOTIFICATION_TYPE.TASK_OVERDUE]: 'tpl_incident_notification',
    [NOTIFICATION_TYPE.TASK_ESCALATED]: 'tpl_incident_notification',
    [NOTIFICATION_TYPE.DOCUMENT_PENDING]: 'tpl_care_statement',
    [NOTIFICATION_TYPE.SETTLING_TRANSITION]: 'tpl_admission_welcome',
    [NOTIFICATION_TYPE.WORKFLOW_COMPLETE]: 'tpl_care_statement',
  };

  static NOTIFICATION_SMS_TEMPLATES = {
    [NOTIFICATION_TYPE.ACER_DEADLINE]: 'sms_emergency',
    [NOTIFICATION_TYPE.TASK_ESCALATED]: 'sms_emergency',
    [NOTIFICATION_TYPE.CREDENTIAL_EXPIRY]: 'sms_credential_expiry',
    [NOTIFICATION_TYPE.ASSESSMENT_OVERDUE]: 'sms_emergency',
  };

  getRules() {
    const stored = LocalStorageService.get(RULES_KEY, null);
    return stored || NotificationEngine.DEFAULT_RULES;
  }

  saveRules(rules) {
    LocalStorageService.set(RULES_KEY, rules);
  }

  // ── SCANNING ──────────────────────────────────────────────────────────

  /**
   * Scan all active residents for ACER deadline alerts.
   * De-duplicates: one alert per (resident, threshold) per day.
   */
  scanAcerDeadlines(facilityId) {
    const rules = this.getRules().filter(
      (r) => r.type === NOTIFICATION_TYPE.ACER_DEADLINE && r.enabled
    );
    if (rules.length === 0) return [];

    const residents = mockResidents.filter(
      (r) => r.facility === facilityId && r.status === 'Active' && r.admissionDate
    );
    const today = todayIso();
    const created = [];

    for (const resident of residents) {
      const sla = acerSlaFromAdmission(resident.admissionDate, today);
      if (sla.daysUntilAcerDue > 28) continue; // not yet relevant

      for (const rule of rules) {
        if (sla.daysUntilAcerDue <= rule.daysThreshold && sla.daysUntilAcerDue > 0) {
          const dedupeKey = `acer_${resident.id}_${rule.id}_${today}`;
          if (this._isDuplicate(dedupeKey)) continue;

          const n = this.create({
            facilityId,
            residentId: resident.id,
            type: NOTIFICATION_TYPE.ACER_DEADLINE,
            severity: rule.severity,
            title: `ACER deadline: ${sla.daysUntilAcerDue} days remaining`,
            message: `${resident.name} — ACER lodgement due in ${sla.daysUntilAcerDue} days (admission ${resident.admissionDate}).`,
            channels: [NOTIFICATION_CHANNEL.IN_APP, NOTIFICATION_CHANNEL.EMAIL],
            metadata: { dedupeKey, ruleId: rule.id, daysRemaining: sla.daysUntilAcerDue },
          });
          created.push(n);
        }
      }
    }
    return created;
  }

  /**
   * Scan workflow tasks for overdue items and escalate to facility manager.
   */
  scanOverdueTasks(facilityId) {
    const residents = mockResidents.filter((r) => r.facility === facilityId);
    const today = todayIso();
    const created = [];

    for (const resident of residents) {
      const tasks = listTasks(resident.id);
      const overdue = tasks.filter((t) => t.status === TASK_STATUS.OVERDUE);

      for (const task of overdue) {
        const dedupeKey = `overdue_${task.id}_${today}`;
        if (this._isDuplicate(dedupeKey)) continue;

        const daysOverdue = Math.floor(
          (new Date() - new Date(task.dueAt)) / (86400000)
        );

        let severity = NOTIFICATION_SEVERITY.WARNING;
        if (daysOverdue > 7) severity = NOTIFICATION_SEVERITY.CRITICAL;

        const n = this.create({
          facilityId,
          residentId: resident.id,
          type: NOTIFICATION_TYPE.TASK_OVERDUE,
          severity,
          title: `Overdue task: ${task.title}`,
          message: `${resident.name} — "${task.title}" is ${daysOverdue} day(s) overdue (due ${task.dueAt}). Owner: ${task.ownerRole || 'unassigned'}.`,
          channels: [NOTIFICATION_CHANNEL.IN_APP],
          metadata: { dedupeKey, taskId: task.id, daysOverdue, ownerRole: task.ownerRole },
        });
        created.push(n);
      }
    }
    return created;
  }

  /**
   * Scan for credential expiry alerts (staff).
   * Uses mock staff data credentials.
   */
  scanCredentialExpiry(facilityId, staffList = []) {
    const rules = this.getRules().filter(
      (r) => r.type === NOTIFICATION_TYPE.CREDENTIAL_EXPIRY && r.enabled
    );
    if (rules.length === 0) return [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const created = [];

    for (const staff of staffList) {
      const credentials = staff.credentials || [];
      for (const cred of credentials) {
        if (!cred.expiryDate) continue;
        const expiry = new Date(cred.expiryDate);
        expiry.setHours(0, 0, 0, 0);
        const daysUntil = Math.ceil((expiry - today) / 86400000);

        for (const rule of rules) {
          if (daysUntil <= rule.daysThreshold && daysUntil > 0) {
            const dedupeKey = `cred_${staff.id}_${cred.type}_${rule.id}_${todayIso()}`;
            if (this._isDuplicate(dedupeKey)) continue;

            const n = this.create({
              userId: staff.id,
              facilityId,
              type: NOTIFICATION_TYPE.CREDENTIAL_EXPIRY,
              severity: rule.severity,
              title: `Credential expiring: ${cred.type}`,
              message: `${staff.name} — ${cred.type} expires in ${daysUntil} days (${cred.expiryDate}).`,
              channels: [NOTIFICATION_CHANNEL.IN_APP, NOTIFICATION_CHANNEL.EMAIL],
              metadata: { dedupeKey, ruleId: rule.id, staffId: staff.id, credentialType: cred.type },
            });
            created.push(n);
          }
        }
      }
    }
    return created;
  }

  /**
   * Run all scans for a facility.
   */
  runFullScan(facilityId, staffList = []) {
    const results = {
      acer: this.scanAcerDeadlines(facilityId),
      overdueTasks: this.scanOverdueTasks(facilityId),
      credentials: this.scanCredentialExpiry(facilityId, staffList),
    };

    if (process.env.NODE_ENV === 'development') {
      const total = results.acer.length + results.overdueTasks.length + results.credentials.length;
      console.debug('[NotificationEngine] full scan', { facilityId, total, breakdown: {
        acer: results.acer.length,
        overdueTasks: results.overdueTasks.length,
        credentials: results.credentials.length,
      }});
    }

    return results;
  }

  // ── INTERNAL ──────────────────────────────────────────────────────────

  /**
   * Dispatch a notification to external channels (email, SMS).
   * In-app is already handled by _emit(). Each channel is independently
   * wrapped so a failure in one doesn't block the others.
   */
  _dispatch(notification) {
    const { channels, type, severity } = notification;

    if (channels.includes(NOTIFICATION_CHANNEL.EMAIL)) {
      const templateId = NotificationEngine.NOTIFICATION_EMAIL_TEMPLATES[type];
      if (templateId) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[NotificationEngine] dispatching EMAIL', {
              notificationId: notification.id, templateId, to: notification.userId,
            });
          }
          const templateVars = this._buildEmailVariables(notification);
          EmailService.send({
            to: notification.metadata?.recipientEmail || `${notification.userId}@medfastcrm.com.au`,
            templateId,
            templateVariables: templateVars,
            category: 'notification',
            priority: severity === NOTIFICATION_SEVERITY.CRITICAL ? 'high' : 'normal',
            facilityId: notification.facilityId,
            residentId: notification.residentId,
            staffId: notification.metadata?.staffId || null,
          }).catch((err) => {
            console.error('[NotificationEngine] EMAIL dispatch failed', notification.id, err);
          });
        } catch (err) {
          console.error('[NotificationEngine] EMAIL dispatch error', notification.id, err);
        }
      }
    }

    if (channels.includes(NOTIFICATION_CHANNEL.SMS)) {
      if (severity !== NOTIFICATION_SEVERITY.CRITICAL) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[NotificationEngine] SMS skipped (non-critical)', notification.id);
        }
      } else {
        const smsTemplateId = NotificationEngine.NOTIFICATION_SMS_TEMPLATES[type] || 'sms_emergency';
        try {
          if (process.env.NODE_ENV === 'development') {
            console.debug('[NotificationEngine] dispatching SMS', {
              notificationId: notification.id, smsTemplateId, to: notification.userId,
            });
          }
          const templateVars = this._buildSmsVariables(notification);
          SMSService.send({
            to: notification.metadata?.recipientPhone || '+61400000001',
            templateId: smsTemplateId,
            templateVariables: templateVars,
            category: 'notification',
            priority: 'critical',
            facilityId: notification.facilityId,
            residentId: notification.residentId,
            staffId: notification.metadata?.staffId || null,
          }).catch((err) => {
            console.error('[NotificationEngine] SMS dispatch failed', notification.id, err);
          });
        } catch (err) {
          console.error('[NotificationEngine] SMS dispatch error', notification.id, err);
        }
      }
    }
  }

  _buildEmailVariables(notification) {
    return {
      recipientName: notification.userId || 'Team',
      residentName: notification.metadata?.residentName || 'Resident',
      facilityName: notification.facilityId || 'MedFast Facility',
      staffName: notification.metadata?.staffName || notification.userId || 'Staff Member',
      credentialType: notification.metadata?.credentialType || '',
      expiryDate: notification.metadata?.expiryDate || '',
      coordinatorName: notification.metadata?.coordinatorName || 'Care Team',
      incidentSummary: notification.message || '',
      month: new Date().toLocaleString('default', { month: 'long' }),
      year: new Date().getFullYear().toString(),
    };
  }

  _buildSmsVariables(notification) {
    return {
      residentName: notification.metadata?.residentName || 'Resident',
      facilityName: notification.facilityId || 'MedFast Facility',
      facilityPhone: notification.metadata?.facilityPhone || '1300 MEDFAST',
      emergencyType: notification.title || notification.type,
      credentialType: notification.metadata?.credentialType || '',
      expiryDate: notification.metadata?.expiryDate || '',
      updateSummary: notification.message || '',
    };
  }

  _isDuplicate(dedupeKey) {
    const items = LocalStorageService.get(STORAGE_KEY, []);
    return items.some((n) => n.metadata?.dedupeKey === dedupeKey);
  }
}

const engine = new NotificationEngine();
export default engine;
